import styled from "styled-components";
import { ErrorRecord, OptionsArray, OptionsObject, Product, Color } from "../../types";
import {
    Box,
    CardActions,
    Chip,
    DialogActions,
    ImageList,
    ImageListItem,
    Typography,
} from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { SoecoButton } from "../../components/SoecoButton";
import Dialog from "@mui/material/Dialog";
import { useContext, useEffect, useMemo, useState } from "react";
import { useGetProductCategories } from "../../hooks/productCategoryHooks";
import { useGetProductColors } from "../../hooks/productColorHooks";
import { useDeleteProduct, useGetProduct, useUpdateProduct } from "../../hooks/productHooks";
import { useNavigate, useParams } from "react-router-dom";
import { useGetDesigners } from "../../hooks/designerHooks";
import { useGetManufacturers } from "../../hooks/manufacturerHooks";
import { useGetProductStatuses } from "../../hooks/productStatusHooks";
import { useGetActions } from "../../hooks/actionHooks";
import { useGetMaterials } from "../../hooks/materialHooks";
import { ExistingImageList } from "./ExistingImageList";
import { EditProductCategory } from "./EditProductCategory";
import { EditDetails } from "./EditDetails";
import { validateChanges } from "./validateChanges";
import { EditPrices } from "./EditPrices";
import { EditFactory } from "./EditFactory";
import { imagesUrl } from "../../services/endpoints";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../../components/Modals/Modal";
import { ErrorModal } from "../../components/Modals/ErrorModal";
import { useSnackBar } from "../../context/SnackbarContext";
import {
    createReadOnlyOptions,
    createReadOnlySortedOptions,
    normalizeColors,
    createReadOnlyMaterialsOptions,
    createReadOnlyProductStatusesOptions,
} from "../../utils/util";
import {
    Image,
    Shapes,
    FileText,
    CircleDollarSign,
    Building2,
    Trash2,
    Edit3,
    Copy,
} from "lucide-react";
import { SoecoHeader } from "../../components/SoecoHeader";
import { EmployeeContext } from "../../context/EmployeeContext";
import { useGetEmployee } from "../../hooks/employeeHooks";

export const EditProduct = () => {
    const { companyId, inventoryId, productId } = useParams();
    const { employee } = useContext(EmployeeContext);
    const employeeQuery = useGetEmployee(employee.companyId, employee.id);
    const navigate = useNavigate();
    const updateMutation = useUpdateProduct();
    const deleteMutation = useDeleteProduct();

    const [imageUrls, setImageUrls] = useState<string[]>([]);
    const [images, setImages] = useState<File[]>([]);
    const [newImages, setNewImages] = useState<File[]>([]);

    const newImageUrls = useMemo(() => {
        return newImages.map(file => URL.createObjectURL(file));
    }, [newImages]);

    useEffect(() => {
        return () => {
            newImageUrls.forEach((url: string) => URL.revokeObjectURL(url));
        };
    }, [newImageUrls]);

    const combinedImageUrls = useMemo(() => {
        const uniqueUrls = new Set([...imageUrls, ...newImageUrls]);
        return Array.from(uniqueUrls);
    }, [imageUrls, newImageUrls]);

    // Show a modal to edit the product categories, details, prices, etc.
    const [modal, setModal] = useState<{
        images: boolean;
        categories: boolean;
        details: boolean;
        prices: boolean;
        factories: boolean;
        delete: boolean;
    }>({
        images: false,
        categories: false,
        details: false,
        prices: false,
        factories: false,
        delete: false,
    });

    const handleModal = (key: keyof typeof modal) => {
        setModal({
            ...modal,
            [key]: !modal[key],
        });
    };

    const { data: productData } = useGetProduct(
        companyId ?? "",
        inventoryId ?? "",
        productId ?? "",
    );

    const { data: manufacturersData } = useGetManufacturers();
    const { data: designersData } = useGetDesigners();
    const { data: statusesData } = useGetProductStatuses();
    const { data: productCategories } = useGetProductCategories();
    const { data: actionsData } = useGetActions();
    const { data: colors } = useGetProductColors();
    const { data: materialsData } = useGetMaterials();

    const [manufacturers, setManufacturers] = useState<OptionsArray>([]);
    const [designers, setDesigners] = useState<OptionsArray>([]);
    const [productStatuses, setProductStatuses] = useState<OptionsArray>([]);
    const [actions, setActions] = useState<OptionsArray>([]);
    const [materials, setMaterials] = useState<OptionsArray>([]);

    const [manufacturer, setManufacturer] = useState<OptionsObject | null>(null);
    const [designer, setDesigner] = useState<OptionsObject | null>(null);
    const [productStatus, setProductStatus] = useState<OptionsObject | null>(null);
    const [action, setAction] = useState<OptionsObject | null>(null);
    const [material, setMaterial] = useState<OptionsObject | null>(null);

    const [newManufacturer, setNewManufacturer] = useState<OptionsObject | null>(null);
    const [newDesigner, setNewDesigner] = useState<OptionsObject | null>(null);
    const [newProductStatus, setNewProductStatus] = useState<OptionsObject | null>(null);
    const [newAction, setNewAction] = useState<OptionsObject | null>(null);
    const [newMaterial, setNewMaterial] = useState<OptionsObject | null>(null);

    const [error, setError] = useState<boolean>(false);
    const [errors, setErrors] = useState<ErrorRecord<Product>>({});
    const [isChanged, setIsChanged] = useState<boolean>(false);

    const [product, setProduct] = useState<Product>({
        companyId: companyId ?? "",
        inventoryId: inventoryId ?? "",
        name: "",
        categories: [],
        imageUrls: [],
        netPrice: "",
        purchasePrice: "",
        marketPrice: "",
        color: "",
        measurements: "",
        co2e: null,
        comment: "",
        manufacturer: "",
        designer: "",
        status: "",
        warehousePlacement: "",
        town: "",
        roomPlacement: "",
        quantity: 1,
        action: "",
        colors: [],
        material: "",
        height: "",
        width: "",
        depth: "",
        length: "",
        seatHeight: "",
        avalible: "",
        address: "",
    });

    const [changedProduct, setChangedProduct] = useState<Product>({} as Product);

    useEffect(() => {
        if (productData) {
            const product = productData;

            const productColors = normalizeColors(product.color ? product.color : "", colors || []);
            product.netPrice == null && (product.netPrice = "");
            product.purchasePrice == null && (product.purchasePrice = "");
            product.marketPrice == null && (product.marketPrice = "");
            if (productData.colors === null || productData.colors?.length === 0) {
                // If the product is with the cold color, set the color to the product
                product.colors = productColors;
            }
            setProduct(product);
            setChangedProduct(productData);
            productData.imageUrls?.forEach(url => {
                setImageUrls(productData.imageUrls?.map(url => imagesUrl + url) ?? []);
                setImages(productData.imageUrls?.map(url => new File([], url)) ?? []);
            });
        }
    }, [productData, colors]);

    useEffect(() => {
        if (manufacturersData) {
            setManufacturers(createReadOnlySortedOptions(manufacturersData));
            createReadOnlySortedOptions(manufacturersData).forEach(item => {
                if (item.id === product.manufacturer) {
                    setManufacturer(item);
                    setNewManufacturer(item);
                }
            });
        }
    }, [product.manufacturer, manufacturersData]);
    useEffect(() => {
        if (designersData) {
            const a = createReadOnlySortedOptions(designersData);
            setDesigners(a);
            a.forEach(item => {
                if (item.id === product.designer) {
                    setDesigner(item);
                    setNewDesigner(item);
                }
            });
        }
    }, [product.designer, designersData]);
    useEffect(() => {
        if (statusesData) {
            setProductStatuses(createReadOnlyProductStatusesOptions(statusesData));
            createReadOnlyProductStatusesOptions(statusesData).forEach(item => {
                if (item.id === product.status) {
                    setProductStatus(item);
                    setNewProductStatus(item);
                }
            });
        }
    }, [product.status, statusesData]);
    useEffect(() => {
        if (actionsData) {
            setActions(createReadOnlyProductStatusesOptions(actionsData));
            createReadOnlyProductStatusesOptions(actionsData).forEach(item => {
                if (item.id === product.action) {
                    setAction(item);
                    setNewAction(item);
                }
            });
        }
    }, [product.action, actionsData]);
    useEffect(() => {
        if (materialsData) {
            setMaterials(createReadOnlyMaterialsOptions(materialsData));
            // createReadOnlyOptions(materialsData).forEach(item => {
            //     if (item.id === product.material) {
            //         setMaterial(item);
            //         setNewMaterial(item);
            //     }
            // });
            createReadOnlyMaterialsOptions(materialsData).forEach(item => {
                if (item.id === product.material) {
                    setMaterial(item);
                    setNewMaterial(item);
                }
            });
        }
    }, [product.material, materialsData]);

    const handleModalSave = (key: keyof typeof modal) => {
        if (!validateChanges(changedProduct, setErrors, setError, key)) {
            return;
        }
        key === "categories"
            ? setProduct({ ...product, categories: changedProduct.categories })
            : key === "details"
            ? setProduct({
                  ...product,
                  name: changedProduct.name,
                  manufacturer: changedProduct.manufacturer,
                  designer: changedProduct.designer,
                  color: changedProduct.color,
                  measurements: changedProduct.measurements,
                  comment: changedProduct.comment,
                  status: changedProduct.status,
                  warehousePlacement: changedProduct.warehousePlacement,
                  town: changedProduct.town,
                  roomPlacement: changedProduct.roomPlacement,
                  quantity: changedProduct.quantity,
                  action: changedProduct.action,
                  colors: changedProduct.colors,
                  material: changedProduct.material,
                  height: changedProduct.height,
                  width: changedProduct.width,
                  depth: changedProduct.depth,
                  length: changedProduct.length,
                  seatHeight: changedProduct.seatHeight,
                  avalible: changedProduct.avalible,
                  //   address: changedProduct.address,
              })
            : key === "prices"
            ? setProduct({
                  ...product,
                  netPrice: changedProduct.netPrice,
                  purchasePrice: changedProduct.purchasePrice,
                  marketPrice: changedProduct.marketPrice,
                  co2e: changedProduct.co2e,
              })
            : key === "images"
            ? setProduct({ ...product, imageUrls: changedProduct.imageUrls })
            : key === "factories"
            ? setProduct({
                  ...product,
                  town: changedProduct.town,
                  address: changedProduct.address,
                  warehousePlacement: changedProduct.warehousePlacement,
                  roomPlacement: changedProduct.roomPlacement,
                  quantity: changedProduct.quantity,
              })
            : null;
        setIsChanged(true);
        handleModal(key);
    };

    const handleModalCancel = (key: keyof typeof modal) => {
        setChangedProduct(product);
        setNewManufacturer(manufacturer);
        setNewDesigner(designer);
        setNewProductStatus(productStatus);
        setNewAction(action);
        handleModal(key);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setChangedProduct({ ...changedProduct, [e.target.name]: e.target.value });
    };
    const handleProductColorChange = (ids: string[]) => {
        setProduct({
            ...product,
            colors: ids,
        });
        setChangedProduct({
            ...changedProduct,
            colors: ids,
        });
    };
    const handleSelectChange = (
        e: React.SyntheticEvent<Element, Event>,
        value: OptionsObject | null,
        targetField: string,
    ) => {
        value && setChangedProduct({ ...changedProduct, [targetField]: value.id });
        value && targetField === "manufacturer" && setNewManufacturer(value);
        value && targetField === "designer" && setNewDesigner(value);
        value && targetField === "status" && setNewProductStatus(value);
        value && targetField === "action" && setNewAction(value);
        value && targetField === "material" && setNewMaterial(value);
    };

    const snackBar = useSnackBar();

    const { isShown, toggle } = useModal();
    const onConfirm = () => {
        handleDeleteProduct();
    };
    const onCancel = () => toggle();

    const handleSubmit = (e: React.FormEvent<Element>) => {
        e.preventDefault();

        if (!companyId || !inventoryId || !productId) {
            // This cannot happen unless routes has been invalidated
            return;
        }
        if (product.imageUrls?.length === 0 && newImages.length === 0) {
            snackBar.showSnackBar("You must choose atleast one image to the product", "error");
            return;
        }
        updateMutation.mutate(
            { companyId, inventoryId, productId, product, images: newImages },
            {
                onError: error => {
                    if (error instanceof Error) {
                        snackBar.showSnackBar(error.message, "error");
                    }
                },
                onSuccess: () => {
                    snackBar.showSnackBar("Product updated", "success");
                    window.location.href = `/companies/${companyId}/inventories/${inventoryId}`;
                },
            },
        );
    };

    const handleDeleteProduct = () => {
        if (!companyId || !inventoryId || !productId) {
            // This cannot happen unless routes has been invalidated
            return;
        }
        deleteMutation.mutate(
            { companyId, inventoryId, productId },
            {
                onError: error => {
                    if (error instanceof Error) {
                        snackBar.showSnackBar(error.message, "error");
                    }
                },
                onSuccess: () => {
                    toggle();
                    snackBar.showSnackBar("Product deleted", "success");
                    navigate(-1);
                },
            },
        );
    };

    return (
        <>
            <Box display="flex" alignItems="center" justifyContent={"center"}>
                <SoecoHeader>{product?.name || "Product"}</SoecoHeader>
            </Box>

            <EditProductPageWrapper>
                <EditProductWrapper>
                    <Modal
                        isShown={isShown}
                        hide={toggle}
                        headerText="Delete the product?"
                        modalContent={
                            <ErrorModal
                                onConfirm={onConfirm}
                                onCancel={onCancel}
                                message="You will not be able to recover it"
                            />
                        }
                    />
                    <Dialog fullScreen open={modal.images}>
                        <SoecoHeader>{product.name || "Product"}</SoecoHeader>
                        <EditWrapper>
                            <ExistingImageList
                                handleChange={handleChange}
                                error={error}
                                errors={errors}
                                images={images}
                                setImages={setImages}
                                imageUrls={imageUrls}
                                setImageUrls={setImageUrls}
                                newImages={newImages}
                                setNewImages={setNewImages}
                                newImageUrls={newImageUrls}
                                combinedImageUrls={combinedImageUrls}
                                setChangedProduct={setChangedProduct}
                                changedProduct={changedProduct}
                            />
                        </EditWrapper>
                        <ActionButtons>
                            <DialogActions
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: 2,
                                }}
                            >
                                <SoecoButton
                                    fullWidth
                                    color="warning"
                                    onClick={() => handleModalCancel("images")}
                                >
                                    Cancel
                                </SoecoButton>
                                <SoecoButton fullWidth onClick={() => handleModalSave("images")}>
                                    Save
                                </SoecoButton>
                            </DialogActions>
                        </ActionButtons>
                    </Dialog>
                    <Dialog fullScreen open={modal.categories}>
                        <SoecoHeader>{product.name || "Product"}</SoecoHeader>
                        <EditProductCategory
                            changedProduct={changedProduct}
                            setChangedProduct={setChangedProduct}
                            employee={employeeQuery?.data}
                        />
                        <DialogActions>
                            <SoecoButton
                                fullWidth
                                color="warning"
                                onClick={() => handleModalCancel("categories")}
                            >
                                Cancel
                            </SoecoButton>
                            <SoecoButton fullWidth onClick={() => handleModalSave("categories")}>
                                Save
                            </SoecoButton>
                        </DialogActions>
                    </Dialog>
                    <Dialog fullScreen open={modal.details}>
                        <SoecoHeader>{product.name || "Product"}</SoecoHeader>
                        <EditDetails
                            changedProduct={changedProduct}
                            manufacturers={manufacturers}
                            manufacturer={newManufacturer}
                            designers={designers}
                            designer={newDesigner}
                            productStatuses={productStatuses}
                            productStatus={newProductStatus}
                            actions={actions}
                            action={newAction}
                            materials={materials}
                            material={newMaterial}
                            handleChange={handleChange}
                            handleSelectChange={handleSelectChange}
                            error={error}
                            errors={errors}
                            colors={colors || []}
                            handleProductColorChange={handleProductColorChange}
                            employee={employeeQuery?.data}
                        />
                        <DialogActions>
                            <SoecoButton
                                fullWidth
                                color="warning"
                                onClick={() => handleModalCancel("details")}
                            >
                                Cancel
                            </SoecoButton>
                            <SoecoButton fullWidth onClick={() => handleModalSave("details")}>
                                Save
                            </SoecoButton>
                        </DialogActions>
                    </Dialog>
                    <Dialog fullScreen open={modal.prices}>
                        <SoecoHeader>{product.name || "Product"}</SoecoHeader>
                        <EditPrices
                            changedProduct={changedProduct}
                            handleChange={handleChange}
                            error={error}
                            errors={errors}
                        />
                        <DialogActions>
                            <SoecoButton
                                fullWidth
                                color="warning"
                                onClick={() => handleModalCancel("prices")}
                            >
                                Cancel
                            </SoecoButton>
                            <SoecoButton fullWidth onClick={() => handleModalSave("prices")}>
                                Save
                            </SoecoButton>
                        </DialogActions>
                    </Dialog>
                    <Dialog fullScreen open={modal.factories}>
                        <SoecoHeader>{product.name || "Product"}</SoecoHeader>
                        <EditFactory
                            changedProduct={changedProduct}
                            handleChange={handleChange}
                            error={error}
                            errors={errors}
                        />
                        <DialogActions>
                            <SoecoButton
                                fullWidth
                                color="warning"
                                onClick={() => handleModalCancel("factories")}
                            >
                                Cancel
                            </SoecoButton>
                            <SoecoButton fullWidth onClick={() => handleModalSave("factories")}>
                                Save
                            </SoecoButton>
                        </DialogActions>
                    </Dialog>
                    {/* CameraForm */}
                    <EditWrapper>
                        <ProductOverviewWrapper>
                            <CardWrapper>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        borderRadius: "30px",
                                    }}
                                >
                                    <StyledCardActions>
                                        <IconActionsWrapper>
                                            {/* <CameraAltIcon color="primary" fontSize="inherit" /> */}
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "flex-start",
                                                    gap: "10px",
                                                }}
                                            >
                                                <Image size={24} color="black" />
                                                <Typography
                                                    color="#8c8c8c"
                                                    fontSize={18}
                                                    fontFamily={"'Poppins', sans-serif"}
                                                    fontWeight={600}
                                                >
                                                    {product.name || " "}
                                                </Typography>
                                            </div>
                                            <SoecoButton
                                                icon={true}
                                                onClick={() => handleModal("images")}
                                                aria-label="Edit product picture "
                                            >
                                                <Edit3 size={24} color="black" />
                                            </SoecoButton>
                                        </IconActionsWrapper>
                                    </StyledCardActions>
                                    <CardContent>
                                        <ImageListWrapper>
                                            <ImageList cols={1}>
                                                {product?.imageUrls ? (
                                                    <ImageListItem key={product.imageUrls[0]}>
                                                        <img
                                                            src={imagesUrl + product.imageUrls[0]}
                                                            alt="image"
                                                        />
                                                    </ImageListItem>
                                                ) : (
                                                    <></>
                                                )}
                                            </ImageList>
                                        </ImageListWrapper>
                                    </CardContent>
                                </Card>
                            </CardWrapper>
                            {/* ProductCategoryForm */}
                            <CardWrapper>
                                <Card variant="outlined" sx={{ borderRadius: "30px" }}>
                                    <StyledCardActions>
                                        <IconActionsWrapper>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "flex-start",
                                                    gap: "10px",
                                                }}
                                            >
                                                <Shapes size={24} color="black" />
                                                <Typography
                                                    color="#8c8c8c"
                                                    fontSize={18}
                                                    fontFamily={"'Poppins', sans-serif"}
                                                    fontWeight={600}
                                                >
                                                    {"Product Type"}
                                                </Typography>
                                            </div>
                                            <SoecoButton
                                                icon={true}
                                                onClick={() => handleModal("categories")}
                                                aria-label="Edit product type "
                                            >
                                                <Edit3 size={24} color="black" />
                                            </SoecoButton>
                                        </IconActionsWrapper>
                                    </StyledCardActions>
                                    <CardContent>
                                        {/* Map categories to name from productCategories */}
                                        {productCategories ? (
                                            productCategories.map(data => {
                                                if (
                                                    product.categories &&
                                                    product.categories.includes(data.id)
                                                ) {
                                                    return (
                                                        <Chip
                                                            key={data.id}
                                                            // label={data.nameEN}
                                                            label={(() => {
                                                                switch (
                                                                    employeeQuery?.data?.language
                                                                ) {
                                                                    case "en":
                                                                        return data.nameEN;
                                                                    case "no":
                                                                        return data.nameNO;
                                                                    case "dk":
                                                                        return data.nameDK;
                                                                    case "fi":
                                                                        return data.nameFI;
                                                                    case "sv":
                                                                        return data.name;
                                                                    default:
                                                                        return data.nameEN;
                                                                }
                                                            })()}
                                                            color="primary"
                                                            sx={{
                                                                fontFamily:
                                                                    "'Yllw Sans a1 241219', sans-serif",
                                                            }}
                                                        />
                                                    );
                                                }
                                            })
                                        ) : (
                                            <></>
                                        )}
                                    </CardContent>
                                </Card>
                            </CardWrapper>
                            {/* DetailsForm */}
                            <CardWrapper>
                                <Card variant="outlined">
                                    <StyledCardActions>
                                        <IconActionsWrapper>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "flex-start",
                                                    gap: "10px",
                                                }}
                                            >
                                                <FileText size={24} color="black" />
                                                <Typography
                                                    color="#8c8c8c"
                                                    fontSize={18}
                                                    fontFamily={"'Poppins', sans-serif"}
                                                    fontWeight={600}
                                                >
                                                    {"Details"}
                                                </Typography>
                                            </div>
                                            <SoecoButton
                                                icon={true}
                                                onClick={() => handleModal("details")}
                                                aria-label="Edit product details "
                                            >
                                                <Edit3 size={24} color="black" />
                                            </SoecoButton>
                                        </IconActionsWrapper>
                                    </StyledCardActions>
                                    <CardContent>
                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                Name:
                                            </Typography>
                                            {product.name ? (
                                                <Typography color="black" sx={YllwSansSx2}>
                                                    {product.name}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>

                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                Supplier:
                                            </Typography>
                                            {product.manufacturer ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {manufacturer?.label}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                Designer:
                                            </Typography>
                                            {product.designer ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {designer?.label}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>

                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                Colors:
                                            </Typography>
                                            {colors ? (
                                                colors.map(data => {
                                                    if (
                                                        product.colors &&
                                                        product.colors.includes(data.id)
                                                    ) {
                                                        return (
                                                            <Chip
                                                                key={data.id}
                                                                // label={data.name}
                                                                label={(() => {
                                                                    switch (
                                                                        employeeQuery?.data
                                                                            ?.language
                                                                    ) {
                                                                        case "en":
                                                                            return data.nameEN;
                                                                        case "no":
                                                                            return data.nameNO;
                                                                        case "dk":
                                                                            return data.nameDK;
                                                                        case "fi":
                                                                            return data.nameFI;
                                                                        case "sv":
                                                                            return data.name;
                                                                        default:
                                                                            return (
                                                                                data.nameEN ||
                                                                                data.name
                                                                            );
                                                                    }
                                                                })()}
                                                                sx={{
                                                                    fontFamily:
                                                                        "'Yllw Sans a1 241219', sans-serif",
                                                                }}
                                                                color="primary"
                                                            />
                                                        );
                                                    }
                                                })
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        {/* </CardContent> */}
                                        {/* MATERIAL */}
                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                Material:
                                            </Typography>
                                            {product.material ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {(() => {
                                                        switch (employeeQuery?.data?.language) {
                                                            case "en":
                                                                return material?.nameEN;
                                                            case "sv":
                                                                return material?.name;
                                                            case "no":
                                                                return material?.nameNO;
                                                            case "dk":
                                                                return material?.nameDK;
                                                            case "fi":
                                                                return material?.nameFI;
                                                            default:
                                                                return material?.nameEN;
                                                        }
                                                    })()}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        {/* </CardContent> */}
                                        <RowWrapper>
                                            <Typography
                                                color={"#8c8c8c"}
                                                sx={{
                                                    fontSize: 16,
                                                    fontFamily: "'Yllw Sans a1 241219', sans-serif",
                                                    fontWeight: 400,
                                                }}
                                            >
                                                Measurements:
                                            </Typography>
                                            {product.height ||
                                            product.width ||
                                            product.depth ||
                                            product.length ||
                                            product.seatHeight ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {product.height ? (
                                                        <Typography sx={YllwSansSx2}>
                                                            Height: <b>{product.height} mm</b>
                                                        </Typography>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    {product.width ? (
                                                        <Typography sx={YllwSansSx2}>
                                                            Width: <b>{product.width} mm</b>
                                                        </Typography>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    {product.depth ? (
                                                        <Typography sx={YllwSansSx2}>
                                                            Depth: <b>{product.depth} mm</b>
                                                        </Typography>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    {product.length ? (
                                                        <Typography sx={YllwSansSx2}>
                                                            Length: <b>{product.length} mm</b>
                                                        </Typography>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    {product.seatHeight ? (
                                                        <Typography sx={YllwSansSx2}>
                                                            Seating Height:{" "}
                                                            <b>{product.seatHeight} mm</b>
                                                        </Typography>
                                                    ) : (
                                                        <></>
                                                    )}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                Status:
                                            </Typography>
                                            {product.status ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {(() => {
                                                        switch (employeeQuery?.data?.language) {
                                                            case "en":
                                                                return productStatus?.name;
                                                            case "no":
                                                                return productStatus?.nameNO;
                                                            case "dk":
                                                                return productStatus?.nameDK;
                                                            case "fi":
                                                                return productStatus?.nameFI;
                                                            case "sv":
                                                                return productStatus?.name;
                                                            default:
                                                                return productStatus?.name || "";
                                                        }
                                                    })()}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                Action:
                                            </Typography>
                                            {product.action ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {(() => {
                                                        switch (employeeQuery?.data?.language) {
                                                            case "en":
                                                                return action?.name;
                                                            case "no":
                                                                return action?.nameNO;
                                                            case "dk":
                                                                return action?.nameDK;
                                                            case "fi":
                                                                return action?.nameFI;
                                                            case "sv":
                                                                return action?.name;
                                                            default:
                                                                return action?.name || "";
                                                        }
                                                    })()}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                Comment:
                                            </Typography>
                                            {product.comment ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {product.comment}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                Available from date:
                                            </Typography>
                                            {product.avalible ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {product.avalible}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                    </CardContent>
                                </Card>
                            </CardWrapper>
                            {/* PriceForm */}
                            <CardWrapper>
                                <Card variant="outlined">
                                    <StyledCardActions>
                                        <IconActionsWrapper>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "flex-start",
                                                    gap: "10px",
                                                }}
                                            >
                                                <CircleDollarSign size={24} color="black" />
                                                <Typography
                                                    color="#8c8c8c"
                                                    fontSize={18}
                                                    fontFamily={"'Poppins', sans-serif"}
                                                    fontWeight={600}
                                                >
                                                    {"Price"}
                                                </Typography>
                                            </div>
                                            <SoecoButton
                                                icon={true}
                                                onClick={() => handleModal("prices")}
                                                aria-label="Edit product price "
                                            >
                                                <Edit3 size={24} color="black" />
                                            </SoecoButton>
                                        </IconActionsWrapper>
                                    </StyledCardActions>
                                    <CardContent>
                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                Sales Price:
                                            </Typography>
                                            {product.netPrice ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {product.netPrice}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                Purchase Price:
                                            </Typography>
                                            {product.purchasePrice ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {product.purchasePrice}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                Market Price:
                                            </Typography>
                                            {product.marketPrice ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {product.marketPrice}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                CO2e (kg):
                                            </Typography>
                                            {product.co2e ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {product.co2e}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                    </CardContent>
                                </Card>
                            </CardWrapper>
                            {/* QuantityLocationForm */}
                            <CardWrapper>
                                <Card variant="outlined">
                                    <StyledCardActions>
                                        <IconActionsWrapper>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "flex-start",
                                                    gap: "10px",
                                                }}
                                            >
                                                <Building2 size={24} color="black" />
                                                <Typography
                                                    color="#8c8c8c"
                                                    fontSize={18}
                                                    fontFamily={"'Poppins', sans-serif"}
                                                    fontWeight={600}
                                                >
                                                    {"Location"}
                                                </Typography>
                                            </div>
                                            <SoecoButton
                                                icon={true}
                                                onClick={() => handleModal("factories")}
                                                aria-label="Edit product location "
                                            >
                                                <Edit3 size={24} color="black" />
                                            </SoecoButton>
                                        </IconActionsWrapper>
                                    </StyledCardActions>
                                    <CardContent>
                                        <RowWrapper>
                                            <Typography color="#8c8c8c" sx={YllwSansSx2}>
                                                Town:
                                            </Typography>
                                            {product.town ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {product.town}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        <RowWrapper>
                                            <Typography color="#8c8c8c" sx={YllwSansSx2}>
                                                Building:
                                            </Typography>
                                            {product.warehousePlacement ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {product.warehousePlacement}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        <RowWrapper>
                                            <Typography color="#8c8c8c" sx={YllwSansSx2}>
                                                Room:
                                            </Typography>
                                            {product.roomPlacement ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {product.roomPlacement}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        <RowWrapper>
                                            <Typography color={"#8c8c8c"} sx={YllwSansSx2}>
                                                Address:
                                            </Typography>
                                            {product.address ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {product.address}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                        <RowWrapper>
                                            <Typography color="#8c8c8c" sx={YllwSansSx2}>
                                                Quantity:
                                            </Typography>
                                            {product.quantity ? (
                                                <Typography sx={YllwSansSx2}>
                                                    {product.quantity}
                                                </Typography>
                                            ) : (
                                                <></>
                                            )}
                                        </RowWrapper>
                                    </CardContent>
                                </Card>
                            </CardWrapper>
                        </ProductOverviewWrapper>
                    </EditWrapper>
                    <ActionButtons>
                        <SoecoButton color="warning" onClick={toggle} aria-label="Delete product ">
                            <Trash2 size={24} color="black" />
                        </SoecoButton>
                        <SoecoButton
                            fullWidth
                            size="large"
                            color="primary"
                            onClick={() => {
                                navigate(-1);
                            }}
                        >
                            Cancel
                        </SoecoButton>
                        <SoecoButton
                            fullWidth
                            size="large"
                            color="warning"
                            onClick={() => {
                                navigate(
                                    `/companies/${product.companyId}/inventories/${product.inventoryId}/products/new`,
                                    { state: { productId: product.id } },
                                );
                            }}
                            aria-label="Copy product "
                        >
                            <Copy size={24} />
                        </SoecoButton>
                        <SoecoButton
                            disabled={!isChanged || updateMutation.isPending}
                            fullWidth
                            size="large"
                            type="submit"
                            color="secondary"
                            onClick={(e: React.FormEvent<Element>) => handleSubmit(e)}
                        >
                            Save
                        </SoecoButton>
                    </ActionButtons>
                </EditProductWrapper>
            </EditProductPageWrapper>
        </>
    );
};

const EditProductPageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 80vh;
    justify-content: center;
    align-items: center;
    gap: 20px;
`;

const EditProductWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 95%;
    height: 100%;
    justify-content: space-between;
    gap: 2rem;
`;

const EditWrapper = styled.div`
    display: flex;
    flex-direction: column;
    heigth: 100%;
    max-heigth: 80%;
    overflow: scroll;
`;

export const ActionButtons = styled.div`
    display: flex;
    width: 95%;
    gap: 1rem;
    position: fixed;
    bottom: 2rem;
`;

const ProductOverviewWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    gap: 1rem;
`;

const CardWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const ImageListWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

export const RowWrapper = styled.div`
    display: flex;
    flex-direction: row;
    gap: 2px;
    margin: 10px;
    > p:first-of-type {
        min-width: 150px;
        text-align: left;
        color: #757575;
        font-weight: 500;
    }
    > p:last-of-type {
        flex: 1;
        word-break: break-word;
    }
`;

const IconActionsWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
`;

const DialogTitleWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    margin-top: 1rem;
    margin-bottom: 1rem;
    font-size: 2.5rem;
`;

const StyledCardActions = styled(CardActions)`
    display: flex;
    width: 100%;
    font-size: 3rem;
    border-bottom: 1px solid #ebebeb;
    align-items: center;
`;
const YllwSansSx = {
    fontFamily: "'Yllw Sans a1 241219', sans-serif",
};
const YllwSansSx2 = {
    fontSize: 16,
    fontFamily: "'Yllw Sans a1 241219', sans-serif",
    fontWeight: 400,
};
